import { resolveBackgroundRemoval } from './render-task-contract.js'

const VIDEO_MIME_CANDIDATES = [
  'video/mp4;codecs=avc1.42E01E',
  'video/mp4',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm'
]

const normalizedValue = value => String(value || '').trim().toLowerCase()

export function resolveVideoRecordingMode(task, trajectoryLength = 0) {
  const modelType = normalizedValue(task?.model_type ?? task?.modelType)
  const pipeline = normalizedValue(task?.pipeline_type ?? task?.pipelineType)
  if (resolveBackgroundRemoval(task) === true) return 'orbit'
  if (Number(trajectoryLength) >= 2) return 'trajectory'
  if (modelType === 'object' || pipeline === 'ai_gen_object') return 'orbit'
  return ''
}

export function resolveCanvasRecorderMimeType(MediaRecorderClass = globalThis.MediaRecorder) {
  if (typeof MediaRecorderClass !== 'function') return ''
  if (typeof MediaRecorderClass.isTypeSupported !== 'function') return VIDEO_MIME_CANDIDATES[VIDEO_MIME_CANDIDATES.length - 1]
  return VIDEO_MIME_CANDIDATES.find(type => MediaRecorderClass.isTypeSupported(type)) || ''
}

export function videoExtensionForMimeType(mimeType) {
  return normalizedValue(mimeType).includes('mp4') ? 'mp4' : 'webm'
}

export function supportsCanvasVideoRecording(canvas, MediaRecorderClass = globalThis.MediaRecorder) {
  return Boolean(typeof canvas?.captureStream === 'function' && resolveCanvasRecorderMimeType(MediaRecorderClass))
}

export function orbitPositionAtProgress(position, focus, progress) {
  const angle = -Math.max(0, Math.min(1, Number(progress) || 0)) * Math.PI * 2
  const x = Number(position?.x) - Number(focus?.x)
  const y = Number(position?.y) - Number(focus?.y)
  const z = Number(position?.z) - Number(focus?.z)
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  return {
    x: Number(focus?.x) + x * cos + z * sin,
    y: Number(focus?.y) + y,
    z: Number(focus?.z) - x * sin + z * cos
  }
}

export async function startCanvasVideoRecording(canvas, options = {}) {
  const MediaRecorderClass = options.MediaRecorderClass || globalThis.MediaRecorder
  const mimeType = resolveCanvasRecorderMimeType(MediaRecorderClass)
  if (!canvas?.captureStream || !mimeType) throw new Error('video_recording_not_supported')

  const fps = Math.max(1, Math.min(60, Number(options.fps) || 30))
  const stream = canvas.captureStream(fps)
  const stopTracks = () => stream.getTracks?.().forEach(track => track.stop?.())
  let recorder
  try {
    recorder = new MediaRecorderClass(stream, {
      mimeType,
      videoBitsPerSecond: Math.max(500_000, Number(options.videoBitsPerSecond) || 6_000_000)
    })
  } catch (error) {
    stopTracks()
    throw error
  }
  const chunks = []
  let settled = false
  let resolveResult
  let rejectResult
  const result = new Promise((resolve, reject) => {
    resolveResult = resolve
    rejectResult = reject
  })

  recorder.ondataavailable = event => {
    if (event.data?.size) chunks.push(event.data)
  }
  recorder.onerror = event => {
    if (settled) return
    settled = true
    stopTracks()
    rejectResult(event.error || new Error('video_recording_failed'))
  }
  recorder.onstop = () => {
    if (settled) return
    settled = true
    stopTracks()
    const blob = new Blob(chunks, { type: recorder.mimeType || mimeType })
    if (!blob.size) rejectResult(new Error('video_recording_empty'))
    else resolveResult(blob)
  }
  try {
    recorder.start(250)
  } catch (error) {
    stopTracks()
    throw error
  }

  return {
    mimeType: recorder.mimeType || mimeType,
    extension: videoExtensionForMimeType(recorder.mimeType || mimeType),
    result,
    stop() {
      if (recorder.state !== 'inactive') recorder.stop()
      return result
    }
  }
}
